import * as Dialog from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '@/utils';

export interface ModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showOverlay?: boolean;
  title?: string;
  hideTitle?: boolean;
  description?: string;
  hideDescription?: boolean;
}

export interface ModalTriggerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  modal: React.ReactNode;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen = false,
      onClose,
      className,
      children,
      size = 'md',
      showOverlay = true,
      title,
      hideTitle = false,
      description,
      hideDescription = true,
      ...props
    },
    ref
  ) => {
    return (
      <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose?.()}>
        <Dialog.Portal>
          {showOverlay ? (
            <Dialog.Overlay className="fixed inset-0 z-(--z-overlay) bg-black/50" />
          ) : (
            <></>
          )}
          <Dialog.Content
            ref={ref}
            className={cn(
              'fixed left-1/2 top-1/2 z-(--z-modal) max-h-[97vh] -translate-x-1/2 -translate-y-1/2 bg-magical-moonlight-white shadow-lg rounded-lg mx-4 overflow-auto',
              sizeClasses[size],
              className
            )}
            {...props}
          >
            {title ? (
              <Dialog.Title
                className={cn('sr-only', !hideTitle && 'not-sr-only')}
              >
                {title}
              </Dialog.Title>
            ) : null}
            {description ? (
              <Dialog.Description
                className={cn('sr-only', !hideDescription && 'not-sr-only')}
              >
                {description}
              </Dialog.Description>
            ) : null}
            {children}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

const ModalTrigger = React.forwardRef<HTMLDivElement, ModalTriggerProps>(
  ({ className, children, modal, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    const modalElement = React.isValidElement(modal)
      ? React.cloneElement(modal as React.ReactElement<ModalProps>, {
          isOpen,
          onClose: () => setIsOpen(false),
        })
      : modal;

    return (
      <div ref={ref} className={cn('inline-block', className)} {...props}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          {children}
        </div>
        {modalElement}
      </div>
    );
  }
);

Modal.displayName = 'Modal';
ModalTrigger.displayName = 'ModalTrigger';

export { Modal, ModalTrigger };
export default Modal;
